import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, inject, input, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { disableFormControlsBasedOnStatus, SessionQuery, SessionStore } from '../../../../../core/state';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003ApplicationInfoStore,
  Cmg1003BorrowerQuery,
  Cmg1003BorrowerStore,
  Cmg1003StoreQueryService,
} from '../../../../../loan-state';
import { resetValidations, canUseUnsavedChanges, getUniqueId } from '../../../../utils';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BorrowerAddressTypes } from '../../../../../cmg-1003/models/borrower-address-types.enum';
import { BorrowerModel } from '../../../../../cmg-1003/models/borrower.model';
import { Cmg1003SectionTypes } from '../../../../../cmg-1003/models/section-types.enum';
import { CmgUiUtilities } from '../../../../../cmg-1003/components/common/cmg-ui-utils';
import { BorrowerAddressChildComponent } from '../borrower-address-child/borrower-address-child.component';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Cmg1003SubSectionTypes } from '../../../../../cmg-1003/models/sub-section-types.enum';
import { LoanSummaryComponentActions } from 'apps/cmg-clear2-ui/src/app/loan-summary/components/common/loan-summary-component-actions';
import { Panel } from 'primeng/panel';
import { PrimeTemplate } from 'primeng/api';

import { Button } from 'primeng/button';
import { CmgFormControlDomDirective } from '../../../../directives/cmg-form-control-dom.directive';

@Component({
  selector: 'cmg-borrower-address-main',
  templateUrl: './borrower-address-main.component.html',
  styleUrls: ['./borrower-address-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: LoanSummaryComponentActions, useExisting: BorrowerAddressMainComponent }],
  imports: [ReactiveFormsModule, Panel, PrimeTemplate, Button, BorrowerAddressChildComponent, CmgFormControlDomDirective],
})
export class BorrowerAddressMainComponent implements OnInit, OnDestroy, LoanSummaryComponentActions {
  private readonly _formBuilder = inject(UntypedFormBuilder);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _borrowerStore = inject(Cmg1003BorrowerStore);
  private readonly _applicationStore = inject(Cmg1003ApplicationInfoStore);
  private readonly _applicationQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _environment = inject(APP_ENV);

  readonly focusedElement = viewChild('focusedElement', { read: ElementRef });
  readonly presentAddress = viewChild<BorrowerAddressChildComponent>('presentAddress');

  @Input()
  borrower: BorrowerModel;

  /** For QA. The automation ID prefix, formatted as "borrowers-[index]-". */
  readonly autoIdPrefix = input<string>(undefined);

  @Input()
  set hasAdditionalBorrower(hasAdditionalBorrower: boolean) {
    this._hasAdditionalBorrower = hasAdditionalBorrower;
    this.allowAdditionalBorrower = !hasAdditionalBorrower;
    this.onAdditionalBorrowerChanged();
  }

  formDisabled = false;
  isSoftLockedStatus = true;
  _hasAdditionalBorrower: boolean;

  BorrowerAddressTypes = BorrowerAddressTypes;
  borrowerAddressForm: UntypedFormGroup;

  isAdditionalBorrowerPresent = false;
  allowAdditionalBorrower = false;
  formChangesSub: Subscription;
  ngRequiredValidator = Validators.required;
  creditUrl = null;

  /**
   * This subscription should only be managed within `_updateBorrower()`.
   * Internally handles this.borrowerForm.valueChanges, but will be separate from this.formChangesSub.
   */
  updateBorrowerSubscription: Subscription;

  uniqueId = getUniqueId();
  ageFilter = /^[0-9, ]*$/;

  private get _primaryBorrower(): BorrowerModel {
    let primaryBorrower = null;
    // only 1 ever, still calling it with plural b/c return type is array
    const primaryBorrowers = this._borrowerQuery.getAll({
      limitTo: 1,
      filterBy: (entity) => entity.isPrimaryBorrower === true && entity.applicationId === this.borrower.applicationId,
    });

    if (primaryBorrowers.length > 0) {
      primaryBorrower = primaryBorrowers[0];
    }

    return primaryBorrower;
  }

  private _destroyed$ = new Subject();

  get isBorrowerEnabled() {
    const enabled =
      this.isSoftLockedStatus && (this.borrower?.isPrimaryBorrower || (!this.borrower?.isPrimaryBorrower && this.allowAdditionalBorrower));
    return enabled;
  }

  getSectionType() {
    return null;
  }

  validate(): void {
    CmgUiUtilities.markFormAsDirty(this.borrowerAddressForm);
    CmgUiUtilities.markFormAsTouched(this.borrowerAddressForm);
    this.presentAddress().validate();

    if (this.borrowerAddressForm.invalid) {
      const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(this.borrowerAddressForm);
      const invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

      const subSection = this.borrower.isPrimaryBorrower
        ? Cmg1003SubSectionTypes.primaryBorrowerDetails
        : Cmg1003SubSectionTypes.additionalBorrowerDetails;

      this._sessionStore.setValidationError(Cmg1003SectionTypes.borrowerInformation, invalidControls, subSection);

      // This ensures validation styling is applied after everything is checked.
      this._cdRef.detectChanges();
    }
  }

  resetValidations(): void {
    resetValidations(this.borrowerAddressForm);

    this.presentAddress().resetValidations();

    setTimeout(() => {
      this._cdRef.detectChanges();
    }, 100);
  }

  ngOnInit(): void {
    this.isAdditionalBorrowerPresent = this._applicationQuery.isAdditionalBorrowerPresent(this.borrower.applicationId);
    this.borrowerAddressForm = this._formBuilder.group(
      {
        mailingAddress: [],
        presentAddress: [],
        priorAddresses: [],
      },
      { updateOn: 'blur' },
    );
    this._updateBorrower();
    this._awaitAutoRecoveryDecision();

    this._setupContinuousValidation();

    combineLatest({
      all1003Loaded: this._sessionQuery.select((s) => s.all1003Loaded),
      statusRequiresFullDisabling: this._storeQuery.statusRequiresFullDisabling$(),
      isClear2DisabledForLoan: this._storeQuery.isClear2DisabledForLoan$,
    })
      .pipe(
        filter(({ all1003Loaded }) => {
          return all1003Loaded;
        }),
        map(({ statusRequiresFullDisabling, isClear2DisabledForLoan }) => {
          return statusRequiresFullDisabling || isClear2DisabledForLoan;
        }),
        distinctUntilChanged(),
        takeUntil(this._destroyed$),
      )
      .subscribe((isFullyDisabled) => {
        this.formDisabled = isFullyDisabled;
        this._checkToDisableForm();
      });

    this._storeQuery
      .statusRequiresDisabling$()
      .pipe(takeUntil(this._destroyed$))
      .subscribe((locked) => {
        this.isSoftLockedStatus = !locked;
      });

    if (!this.isAdditionalBorrowerPresent && !this.borrower?.isPrimaryBorrower) {
      this.allowAdditionalBorrower = true;
      this.onAdditionalBorrowerChanged();
    }
  }

  private _setupContinuousValidation() {
    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.borrowerAddressForm.statusChanges.pipe(debounceTime(0), takeUntil(this._destroyed$)).subscribe(() => {
          this._sessionStore.triggerRevalidation();
        });
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next(undefined);
    this._destroyed$.complete();
  }

  onAdditionalBorrowerChanged() {
    this.allowAdditionalBorrower = !this.allowAdditionalBorrower;
    this._applicationStore.setAdditionalBorrowerChecked(this.borrower.applicationId, this.allowAdditionalBorrower);
    if (this.allowAdditionalBorrower) {
      if (this.borrowerAddressForm) {
        this.borrowerAddressForm.enable({ emitEvent: false });
        // these remain disabled always

        this.formChangesSub = this.borrowerAddressForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(this._onBorrowerFormChanges);
      }
    } else {
      if (this.formChangesSub && !this.formChangesSub.closed) {
        this.formChangesSub.unsubscribe();
      }
      if (this.borrowerAddressForm) {
        this.borrowerAddressForm.disable();
      }
    }
  }

  onCopyAddress(addressType: BorrowerAddressTypes) {
    switch (addressType) {
      case BorrowerAddressTypes.Present:
        {
          const updatedAddress = Object.assign({}, this._primaryBorrower.presentAddress);
          const presentAddressId = this.borrower.presentAddressId ? this.borrower.presentAddressId : -CmgUiUtilities.getUniqueNumber();
          updatedAddress.addressId = presentAddressId;
          const updatedBorrower = {
            ...this.borrower,
            ...{
              presentAddress: updatedAddress,
            },
            ...{
              presentAddressId: updatedAddress.addressId,
            },
          };
          this._borrowerStore.update(this.borrower.borrowerId, updatedBorrower);
          this.borrowerAddressForm.get('presentAddress').setValue(updatedBorrower.presentAddress, { emitEvent: false });
        }
        break;
      default:
        break;
    }
  }

  private _checkToDisableForm() {
    if (this.formDisabled || (!this.borrower.isPrimaryBorrower && !this.isAdditionalBorrowerPresent)) {
      disableFormControlsBasedOnStatus([this.borrowerAddressForm], this._storeQuery, null, null, null, this.formDisabled);
      return true;
    }

    return false;
  }

  private readonly _onBorrowerFormChanges = (formValues: UntypedFormGroup) => {
    const updatedBorrower = { ...this.borrower, ...formValues };
    if (updatedBorrower.presentAddress !== null) {
      updatedBorrower.presentAddress = Object.assign(
        {
          addressId: this.borrower.presentAddressId,
        },
        updatedBorrower.presentAddress,
      );
    }
    this._borrowerStore.update(this.borrower.borrowerId, updatedBorrower);
  };

  private _updateBorrower() {
    if (!this.borrowerAddressForm) {
      return;
    }

    // Ensures infinite loop (from valueChanges) doesn't occur if borrower was updated by auto-recovery.
    this.updateBorrowerSubscription?.unsubscribe();

    this._sessionQuery
      .select((t) => t.fileDataId)
      .pipe(take(1))
      .subscribe((fileDataId) => (this.creditUrl = `/${fileDataId}/credit`));
    const dontEmitEvent = { emitEvent: false };
    this.borrowerAddressForm.get('presentAddress').setValue(this.borrower.presentAddress, dontEmitEvent);

    const formDisabled = this._checkToDisableForm();

    if (!formDisabled) {
      this.updateBorrowerSubscription = this.borrowerAddressForm.valueChanges
        .pipe(
          startWith(this.borrowerAddressForm.value),
          pairwise(),
          map(([oldValues, newValues]) => {
            const key = Object.keys(newValues).find((k) => newValues[k] !== oldValues[k]);
            return {
              key: key,
              oldValue: oldValues[key],
              newValue: newValues[key],
            };
          }),
          takeUntil(this._destroyed$),
        )
        .subscribe((changeObj) => {
          // all of this to work around reverting to last valid value & unnecessary updates sent by pInputMask when data is invalid.
          const updateStore = true;
          if (changeObj.key) {
            if (updateStore) {
              this._onBorrowerFormChanges(this.borrowerAddressForm.value);
            } else {
              this.borrowerAddressForm.get(changeObj.key).setValue(changeObj.oldValue, { emitEvent: false, onlySelf: true });
              this.borrowerAddressForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
          }
        });
      //  Focus on primary borrower form element
      if (this.borrower.isPrimaryBorrower) {
        //  setTimeout else focusedElement is undefined
        setTimeout(() => {
          this.focusedElement()?.nativeElement.focus({ preventScroll: true });
        });
      }
    }

    if (!this.borrower.isPrimaryBorrower && !CmgUiUtilities.isIdTemporary(this.borrower.borrowerId)) {
      this.allowAdditionalBorrower = true;
    }
  }

  onAddressDeleted(addressType: BorrowerAddressTypes) {
    switch (addressType) {
      case BorrowerAddressTypes.Present:
        this.borrowerAddressForm.get('presentAddress').setValue(null);
        break;
      default:
        break;
    }
  }

  /**
   * [Auto-Recovery Flow]:
   * 1) Wait for user's decision on auto-recovery modal.
   * 2) User selects "Unsaved Changes" on auto-recovery modal.
   * 3) Borrower store gets updated, via _update1003ForAutoRecovery() in cmg-1003.service.ts.
   * 4) Observable fires (ONCE) to refresh Borrower using latest data.
   */
  private _awaitAutoRecoveryDecision() {
    combineLatest([this._sessionQuery.select((session) => session.needsAutoRecoveryDecision), this._borrowerQuery.dirtyChecker2.isDirty$])
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([needsDecision, isDirty]) => canUseUnsavedChanges(this._sessionStore.getValue(), isDirty)),
        take(1),
        switchMap(() =>
          combineLatest([
            this._borrowerQuery.selectEntity(this.borrower?.borrowerId),
            this._applicationQuery.selectEntity(this.borrower?.applicationId),
          ]).pipe(
            take(1),
            tap(([borrower, application]) => {
              if (this.isAdditionalBorrowerPresent !== application.additionalBorrowerChecked) {
                this.isAdditionalBorrowerPresent = application.additionalBorrowerChecked;
                this.onAdditionalBorrowerChanged();
              }

              this.borrower = borrower;
              this._updateBorrower();
            }),
          ),
        ),
      )
      .subscribe();
  }
}
